import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { contentStringApi, contentStringKeys } from '@/api/skin/contentStringApi';
import { checkPlaceholders, toStringJson } from '@/models/skin/ContentString';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import LocaleValueEditor from '../../common/LocaleValueEditor';
import { useToast } from '../../common/toast/ToastProvider';

interface Props {
    open: boolean;
    brandId: string;
    keyId: string;
    contentKey: string;
    locales: string[];
    brandValues: Record<string, string>;
    defaultValues: Record<string, string>;
    /** Tokens this key's app substitutes at render time, bare. */
    placeholders: string[];
    canUpdate: boolean;
    onClose: () => void;
}

/**
 * Editing one key's text for this brand, one language at a time.
 *
 * The grid is for scanning; this is for writing. Each language shows the key's default beneath the field as
 * reference, so whoever writes a translation can see what they are replacing without leaving the row.
 *
 * Clearing a field does NOT store an empty string - it removes that locale from the override, so the default
 * applies again. Storing "" would render a blank in the app and look deliberate.
 */
const ContentStringDrawer = ({
    open, brandId, keyId, contentKey, locales, brandValues, defaultValues, placeholders, canUpdate, onClose,
}: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [values, setValues] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        for (const locale of locales) initial[locale] = brandValues[locale.toLowerCase()] ?? '';
        return initial;
    });

    const { mutateAsync: save, isPending: saving } = useMutation({
        mutationFn: async () => {
            const json = toStringJson(values);

            // An override with nothing in it is not an override. Deleting the row is what returns the brand to
            // the default; leaving an empty array behind would be a row that means the same thing but reads as
            // a deliberate customisation on every screen that lists them.
            if (JSON.parse(json).length === 0) {
                await contentStringApi.deleteBrandOverride(brandId, keyId);
            } else {
                await contentStringApi.upsertBrandOverride(brandId, keyId, json);
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.contentStringSaved });
            await qc.invalidateQueries({ queryKey: contentStringKeys.brandOverrides(brandId) });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.contentStringSaveError }),
    });

    const { mutateAsync: revertAll, isPending: reverting } = useMutation({
        mutationFn: () => contentStringApi.deleteBrandOverride(brandId, keyId),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.contentStringReverted });
            await qc.invalidateQueries({ queryKey: contentStringKeys.brandOverrides(brandId) });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.contentStringSaveError }),
    });

    const hasAnyOverride = Object.keys(brandValues).length > 0;

    /**
     * A token no app will replace blocks the save, in ANY language - not just the one on screen.
     *
     * Checked across all of them because the mistake is invisible: whoever typed "#point#" into the French
     * text has almost certainly moved on to another tab by the time they hit Save, and the field showing the
     * error is not the field they are looking at. Letting it through means it renders literally to a
     * customer, and nobody finds out until one of them asks what it means.
     */
    const hasUnknownPlaceholder = Object.values(values)
        .some(text => checkPlaceholders(text, placeholders).unknown.length > 0);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            // zIndex.modal (1300), NOT appBar + 1.
            //
            // Frame.tsx sets the AppBar to zIndex.drawer + 1, which is 1201 - it does NOT use the appBar
            // token (1100). So "appBar + 1" evaluates to 1101 and leaves this sheet a hundred BELOW the
            // title bar, still cut off. Deriving from a token the app has overridden elsewhere is the trap.
            //
            // modal is both high enough and the honest description: this is a modal editing surface, and it
            // should sit where dialogs sit rather than at a number picked to clear one specific element.
            sx={{ zIndex: theme => theme.zIndex.modal }}
        >
            <Box sx={{ width: 520, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>{contentKey}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {strings.contentStringDrawerHelp}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    <LocaleValueEditor
                        locales={locales}
                        values={values}
                        onChange={(locale, value) => setValues(v => ({ ...v, [locale]: value }))}
                        fallbacks={defaultValues}
                        fallbackLabel={strings.contentStringDefaultLabel}
                        noFallbackText={strings.contentStringNoDefaultHelp}
                        emptyPlaceholder={strings.contentStringNoDefault}
                        placeholders={placeholders}
                        placeholderLabel={strings.contentStringInsert}
                        unknownPlaceholderText={strings.contentStringUnknownPlaceholder}
                        missingPlaceholderText={strings.contentStringMissingPlaceholder}
                        disabled={!canUpdate}
                    />

                    {/* No "add more languages" hint here. A brand with one declared language sees one tab, and
                        pointing at Brand Details to add more would be worse than saying nothing: this page is
                        customer-facing and Brand Details is super-admin only, so it would send a client to a
                        screen they cannot open. Adding a language is our job, not theirs. */}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button
                        color="warning"
                        disabled={!canUpdate || !hasAnyOverride || reverting || saving}
                        loading={reverting}
                        onClick={() => revertAll()}
                    >
                        {strings.contentStringRevert}
                    </Button>

                    <Stack direction="row" spacing={1}>
                        <Button onClick={onClose} disabled={saving || reverting}>{strings.cancel}</Button>
                        <Button
                            variant="contained"
                            disabled={!canUpdate || saving || reverting || hasUnknownPlaceholder}
                            loading={saving}
                            onClick={() => save()}
                        >
                            {strings.save}
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Drawer>
    );
};

export default ContentStringDrawer;
